import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Appearance, useColorScheme } from 'react-native';
import type { Theme } from '@react-navigation/native';
import { DarkTheme, DefaultTheme } from '@react-navigation/native';
import {
  darkColors,
  lightColors,
  type ColorPalette,
} from '../constants/colors';
import * as settingsService from '../services/settingsService';
import type { AppPreferences, ThemePreference } from '../types/settings';

type PreferencesContextValue = {
  ready: boolean;
  preferences: AppPreferences;
  themePreference: ThemePreference;
  resolvedScheme: 'light' | 'dark';
  colors: ColorPalette;
  navigationTheme: Theme;
  setThemePreference: (theme: ThemePreference) => Promise<void>;
  reloadPreferences: () => Promise<void>;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function applyColorScheme(theme: ThemePreference) {
  // react-native-web's Appearance does not implement setColorScheme.
  if (typeof Appearance.setColorScheme !== 'function') {
    return;
  }
  Appearance.setColorScheme(theme === 'system' ? 'unspecified' : theme);
}

function navigationThemeFor(scheme: 'light' | 'dark', palette: ColorPalette): Theme {
  const base = scheme === 'dark' ? DarkTheme : DefaultTheme;
  return {
    ...base,
    dark: scheme === 'dark',
    colors: {
      ...base.colors,
      primary: palette.primary,
      background: palette.background,
      card: palette.surface,
      text: palette.textPrimary,
      border: palette.border,
      notification: palette.error,
    },
  };
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const systemScheme = useColorScheme();
  const [ready, setReady] = useState(false);
  const [preferences, setPreferences] = useState<AppPreferences>(
    settingsService.getDefaultPreferences(),
  );

  const reloadPreferences = useCallback(async () => {
    const next = await settingsService.getPreferences();
    setPreferences(next);
    setReady(true);
  }, []);

  useEffect(() => {
    reloadPreferences().catch(() => setReady(true));
  }, [reloadPreferences]);

  const setThemePreference = useCallback(async (theme: ThemePreference) => {
    const next = await settingsService.setThemePreference(theme);
    setPreferences(next);
    applyColorScheme(theme);
  }, []);

  useEffect(() => {
    applyColorScheme(preferences.theme);
  }, [preferences.theme]);

  const resolvedScheme: 'light' | 'dark' =
    preferences.theme === 'system'
      ? systemScheme === 'dark'
        ? 'dark'
        : 'light'
      : preferences.theme;

  const palette = resolvedScheme === 'dark' ? darkColors : lightColors;

  const value = useMemo<PreferencesContextValue>(
    () => ({
      ready,
      preferences,
      themePreference: preferences.theme,
      resolvedScheme,
      colors: palette,
      navigationTheme: navigationThemeFor(resolvedScheme, palette),
      setThemePreference,
      reloadPreferences,
    }),
    [
      palette,
      preferences,
      ready,
      reloadPreferences,
      resolvedScheme,
      setThemePreference,
    ],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used inside PreferencesProvider');
  }
  return context;
}

/** Safe for shared components that may render outside the provider in tests. */
export function useAppColors(): ColorPalette {
  const context = useContext(PreferencesContext);
  return context?.colors ?? lightColors;
}
